/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Icon, Typography } from "../..";
import { getGlobal } from "../../../global";
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { bgColor, bgTransparent, border, borderNone, borderRadius, borderRadius4px, boxBorder, displayBlock, displayNone, flexRow, inset, insetX, itemsCenter, mgl, mgr, outlineNone, parseHeight, parseMaxHeight, parseMinHeight, parseMinWidthHeight, parseWidth, parseWidthHeight, pd, pdl, pdx, pdy, pointerEventsNone, positionAbsolute, positionRelative, selfEnd, userSelectNone } from "../../../styles/general";
import { useColor as colors, useTheme } from "../../../theme";
import { classNames, refType as ref, useInput } from "../../../utils";
const {
  colors: {
    system: {
      active: systemActive,
      white: systemWhite,
      disabled: systemDisabled
    },
    semantic: {
      info: semanticInfo
    },
    fill: {
      hover: fillHover,
      disabled: fillDisabled
    },
    line: {
      normal: lineNormal,
      hover: lineHover,
      focus: lineFocus,
      disabled: lineDisabled
    },
    text: {
      main: textMain,
      sub: textSub
    }
  },
  typography: {
    paragraph1
  }
} = useTheme();
const InputBase = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  autoComplete,
  autoFocus,
  autoWidth,
  className,
  defaultValue,
  delayOnChange,
  disabled,
  endIcon,
  endIconProps,
  hoverTooltip,
  iconStyle,
  inputProps,
  inputRef: inputRefProp,
  inputStyle,
  maxRows,
  multiline,
  nonStyle,
  onBlur,
  onChange,
  onFocus,
  onInput,
  onKeyDown,
  onKeyUp,
  placeholder,
  readOnly,
  resize,
  rows,
  startIcon,
  startIconProps,
  status,
  type,
  value: valueProp,
  viewType,
  ...props
}, reference) => {
  if (!reference) reference = useRef(null);
  if (!autoWidth && inputProps !== null && inputProps !== void 0 && inputProps.autoWidth) autoWidth = inputProps.autoWidth;
  const _onChange = e => {
    onChange && onChange(e);
    dynamicWidth(e);
  };
  const {
    bind,
    value
  } = useInput(defaultValue, valueProp, _onChange, onInput, delayOnChange);
  if (!inputRefProp) inputRefProp = useRef(null);
  const inputRef = useRef(null);
  const ref = useRef(null);
  const valueArray = useRef([]);
  const canvas = document.createElement('canvas');
  const styleInputBase = useRef(null);
  const widthInit = useRef(null);
  const isEnabled = !readOnly && !disabled;
  const _InputBaseCSS = InputBaseCSS(readOnly, status);
  const _SingleLineCSS = SingleLineCSS(autoWidth, readOnly);
  const _InputCSS = InputCSS(autoWidth, isEnabled, hoverTooltip);
  const _TypoCSS = TypoCSS(disabled);
  const _TextAreaCSS = TextAreaCSS(resize, readOnly);
  const _MultilineCSS = MultilineCSS(_TextAreaCSS.name, rows, maxRows);
  const _InputBaseIconCSS = InputBaseIconCSS(multiline);

  /* Start handler */
  const onInputTextArea = e => {
    if (!/\d+/.test(rows)) {
      const el = e.target;
      const defaultHeight = 24;
      if (el.value) {
        el.style.height = 0;
        el.style.height = Math.max(defaultHeight, el.scrollHeight) + 'px';
      } else {
        el.style.height = defaultHeight + 'px';
      }
    }
    !!onInput && onInput(e);
  };
  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== '') {
      if (multiline && !/\d+/.test(rows) && inputRef.current) {
        inputRef.current.style.height = 0;
        inputRef.current.style.height = Math.max(24, inputRef.current.scrollHeight) + 'px';
      }
    }
  }, []);
  useEffect(() => {
    if (multiline && !/\d+/.test(rows) && inputRef.current && !(inputRef.current.value || inputRef.current.value === 0)) {
      inputRef.current.style.height = '24px';
    }
    if (autoFocus) {
      inputRef.current.focus();
    }
  }, [multiline]);
  useEffect(() => {
    const defaultHeight = 24;
    if (valueProp !== undefined) {
      valueArray.current.push(valueProp);
      inputRef.current.value = valueProp;
      if (multiline && !/\d+/.test(rows)) {
        inputRef.current.style.height = 0;
        inputRef.current.style.height = Math.max(defaultHeight, inputRef.current.scrollHeight) + 'px';
      } else {
        dynamicWidth({
          target: {
            value: valueProp
          }
        });
      }
    }
    return () => {
      if (valueProp !== undefined && multiline && !/\d+/.test(rows) && inputRef.current) {
        inputRef.current.style.height = defaultHeight + 'px';
      }
    };
  }, [valueProp]);
  /* End handler */

  useImperativeHandle(reference, () => {
    const currentRef = inputRef.current || {};
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const saveInitInfo = refs => {
    if (!autoWidth) return;
    const style = window.getComputedStyle(refs) || refs.currentStyle;
    if (!widthInit.current) widthInit.current = parseInt(style.width);
    styleInputBase.current = style;
  };
  const dynamicWidth = e => {
    if (!autoWidth || !inputRef.current) return;
    if (!canvas || !styleInputBase.current || !inputRef.current) return;
    const context = canvas.getContext('2d');
    context.font = styleInputBase.current.font;
    const {
      width
    } = context.measureText(e.target.value);
    inputRef.current.style.width = `${Math.max(width, widthInit.current)}px`;
  };

  /* Start component */
  const newClass = viewType + (viewType === 'none' || nonStyle ? ' non-style' : '');
  const StartIcon = useMemo(() => {
    let node = startIcon;
    if (typeof node === 'string') {
      node = jsx(Icon, {
        name: startIcon,
        style: iconStyle,
        ...startIconProps
      });
    }
    return jsx("div", {
      css: _InputBaseIconCSS,
      className: classNames('DGN-UI-InputBase-Start-Icon', startIconProps.className),
      ...startIconProps
    }, node);
  }, [startIcon, startIconProps]);
  const EndIcon = useMemo(() => {
    let node = endIcon;
    if (typeof node === 'string') {
      node = jsx(Icon, {
        name: endIcon,
        style: iconStyle,
        ...endIconProps
      });
    }
    return jsx("div", {
      css: _InputBaseIconCSS,
      className: classNames('DGN-UI-InputBase-End-Icon', endIconProps.className),
      ...endIconProps
    }, node);
  }, [endIcon, endIconProps]);
  const MultipleInputComp = jsx("div", {
    ref: ref,
    css: [_InputBaseCSS, _MultilineCSS],
    className: classNames('DGN-UI-InputBase', newClass, className, disabled && 'disabled'),
    ...props
  }, startIcon && StartIcon, jsx("textarea", {
    placeholder: isEnabled ? placeholder : '',
    defaultValue: defaultValue,
    ...inputProps,
    css: _TextAreaCSS,
    readOnly: readOnly,
    style: inputStyle || (inputProps === null || inputProps === void 0 ? void 0 : inputProps.style),
    className: classNames('DGN-UI-InputBase-Textarea', inputProps.className),
    ref: refs => {
      if (!refs) return;
      inputRef.current = refs;
      typeof inputRefProp === 'function' ? inputRefProp(refs) : inputRefProp.current = refs;
    },
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    onInput: onInputTextArea,
    onBlur: onBlur,
    onFocus: onFocus,
    disabled: disabled,
    ...bind
  }), endIcon && EndIcon);
  const InputComp = jsx("div", {
    ref: ref,
    css: [[_InputBaseCSS, _SingleLineCSS]],
    className: classNames('DGN-UI-InputBase', newClass, className, disabled && 'disabled'),
    ...props
  }, startIcon && StartIcon, jsx("input", {
    type: type,
    autoComplete: autoComplete,
    placeholder: isEnabled ? placeholder : '',
    defaultValue: defaultValue,
    ...inputProps,
    readOnly: readOnly,
    style: inputStyle || (inputProps === null || inputProps === void 0 ? void 0 : inputProps.style),
    className: classNames('DGN-UI-InputBase-Input', newClass, inputProps === null || inputProps === void 0 ? void 0 : inputProps.className),
    ref: refs => {
      if (!refs) return;
      inputRef.current = refs;
      typeof inputRefProp === 'function' ? inputRefProp(refs) : inputRefProp.current = refs;
      saveInitInfo(refs);
    },
    css: _InputCSS,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    onInput: onInput,
    onBlur: onBlur,
    onFocus: onFocus,
    disabled: disabled,
    ...bind
  }), !isEnabled && hoverTooltip && jsx(Typography, {
    css: _TypoCSS,
    className: classNames(newClass, inputProps === null || inputProps === void 0 ? void 0 : inputProps.className),
    lineClamp: 1,
    hoverTooltip: true,
    type: 'p1',
    style: {
      lineHeight: '26px',
      width: '100%'
    }
  }, value !== null && value !== void 0 ? value : ''), endIcon && EndIcon);
  /* End component */

  return multiline ? MultipleInputComp : InputComp;
}));
const InputBaseCSS = (readOnly, status) => css`
    ${flexRow};
    ${positionRelative};
    ${boxBorder};
    &:hover,
    &:focus-within {
        .DGN-UI-InputBase-Start-Icon:not(.non-effect) {
            path {
                fill: ${systemActive};
            }
        }
    }
    &.disabled {
        ${pointerEventsNone};
        border-color: ${lineDisabled} !important;
        &::before {
            border-bottom-color: ${lineDisabled} !important;
        }
        &.outlined {
            ${bgColor(fillDisabled)};
        }
        .DGN-UI-InputBase-Start-Icon,
        .DGN-UI-InputBase-End-Icon {
            &:not(.non-effect),
            &.allow-disabled {
                path {
                    fill: ${systemDisabled};
                }
            }
        }
    }
    &.outlined {
        ${border(1, lineNormal)};
        ${borderRadius4px};
        ${pd([1.75, 4])};
        &:not(:focus-within):hover {
            ${!readOnly && `
                background-color: ${fillHover};
                border-color: ${lineHover};
                input, textarea {
                    ::placeholder {
                        color: ${systemActive};
                    }
                }
            `};
        }
        &:focus-within {
            ${!readOnly && `
                border-color: ${lineFocus};
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
                &::after {
                    border-color: inherit;
                }
            `}
        }
        &::after {
            content: '';
            ${positionAbsolute};
            ${borderRadius4px};
            ${pointerEventsNone};
            ${border(2, 'transparent')};
            ${inset(-2)};
        }
    }
    &.underlined {
        ${pdy([1])};
        &:not(:focus-within):hover {
            ${!readOnly && `
                &::before {
                border-bottom-color: ${lineHover};
                }
            `};
            input,
            textarea {
                ::placeholder {
                    color: ${systemActive};
                }
            }
        }
        &:focus-within {
            ${!readOnly && `
				&::after {
					border-bottom-color: ${lineFocus};
					transform: scaleX(1);
				}
			`}
        }
        &::before {
            content: '';
            ${positionAbsolute};
            ${insetX(0)};
            bottom: 0;
            border-bottom: 1px solid ${lineNormal};
        }
        &::after {
            content: '';
            ${positionAbsolute};
            ${insetX(0)};
            bottom: -1px;
            border-bottom: 2px solid transparent;
            transform: scaleX(0);
            transform-origin: center;
            transition: all 0.2s ease;
        }
    }
    &.non-style {
        ${pdy([1])};
        border-color: transparent !important;
        box-shadow: none !important;
        background-color: transparent !important;
        input,
        textarea {
            background-color: transparent !important;
        }
        &:before,
        &:after {
            border-color: transparent !important;
        }
    }
    ${status !== 'default' && `
        border-color: ${colors[status]} !important;
        &::before {
            border-color: ${colors[status]} !important;
    }`}
    & + .DGN-UI-HelperText {
        ${positionAbsolute};
        ${parseMinHeight(16)};
        top: 100%;
    }
`;
const SingleLineCSS = (autoWidth, readOnly) => css`
    ${parseWidthHeight(autoWidth ? 'max-content' : 'auto', 'max-content')}
    ${itemsCenter};
    flex: 1 1 auto;
    &:focus-within {
        ${!readOnly && `
            border-bottom-color: ${semanticInfo};
            &::after {
                border-bottom-color: inherit;
                transform: scaleX(1);
            }
        `}
    }
    &.outlined {
        ${parseHeight(40)};
        ${bgColor(systemWhite)};
    }
`;
const InputCSS = (autoWidth, isEnabled, hoverTooltip) => css`
    ${parseWidthHeight(autoWidth ? '10px' : '100%', 24)};
    ${borderNone};
    ${outlineNone};
    transition: all 0.1s;
    color: ${textMain};
    ${bgTransparent};
    ${pdy(0)};
    ${paragraph1};
    text-overflow: ellipsis;
    &::placeholder {
        color: ${textSub};
        ${paragraph1};
    }
    &:focus {
        ${bgTransparent};
        color: ${systemActive};
    }
    &:hover {
        color: ${systemActive};
    }
    &.underlined {
        ${pdx(0)};
    }
    &:disabled {
        ${bgTransparent};
    }
    ${!isEnabled && hoverTooltip && displayNone};
`;
const TypoCSS = disabled => css`
    ${parseHeight(24)};
    &:focus {
        ${bgTransparent};
        color: ${systemActive};
    }
    &:hover {
        color: ${systemActive};
    }
    ${disabled && userSelectNone};
`;
const MultilineCSS = (TextAreaCSSName, rows, maxRows) => css`
    ${parseWidthHeight('100%', 'max-content')};
    &.outlined {
        .css-${TextAreaCSSName} {
            ${parseHeight(24)};
            ${rows && /\d+/.test(rows) && parseHeight(20 * rows + 4)};
            ${maxRows && /\d+/.test(maxRows) && parseMaxHeight(20 * maxRows + 4)};
        }
    }
    &.underlined {
        .css-${TextAreaCSSName} {
            ${parseHeight(24)};
            ${rows && /\d+/.test(rows) && parseHeight(20 * rows + 4)};
            ${maxRows && /\d+/.test(maxRows) && parseMaxHeight(20 * maxRows + 4)};
        }
    }
`;
const TextAreaCSS = (resize, readOnly) => css`
    ${displayBlock};
    ${paragraph1};
    ${borderNone};
    ${outlineNone};
    ${positionRelative};
    ${boxBorder};
    ${parseWidth('100%')};
    ${parseMinWidthHeight('max-content')};
    ${pd([0.5, 0])};
    ${bgTransparent};
    color: ${textMain};
    overflow-y: auto;
    resize: ${resize};
    &:hover:not(:focus-within) {
        ${!readOnly && `
			border-color: ${systemActive};
			color: ${systemActive};
			::placeholder {
				color: ${systemActive};
			}
		`};
    }
    &:focus-within {
        color: ${systemActive};
    }
    &::-webkit-scrollbar {
        ${borderRadius4px};
        ${parseWidth(4)};
        ${bgColor(systemWhite)};
    }
    &::-webkit-scrollbar-thumb {
        ${borderRadius(10)};
        ${bgColor(fillDisabled)};
        mix-blend-mode: normal;
        background-clip: content-box;
    }
`;
const InputBaseIconCSS = multiple => css`
    ${flexRow};
    ${multiple && selfEnd};
    color: inherit;
    &.DGN-UI-InputBase-Start-Icon {
        ${mgr([1])};
        & + input {
            ${pdl(0)};
        }
    }
    &.DGN-UI-InputBase-End-Icon {
        ${mgl([1])};
    }
`;
InputBase.defaultProps = {
  autoComplete: 'off',
  autoFocus: false,
  className: '',
  defaultValue: '',
  disabled: false,
  endIconProps: {},
  hoverTooltip: false,
  iconStyle: {},
  inputProps: {},
  inputStyle: {},
  multiline: false,
  nonStyle: false,
  placeholder: getGlobal('inputPlaceholder'),
  readOnly: false,
  required: false,
  resize: 'none',
  startIconProps: {},
  status: 'default',
  type: 'text',
  viewType: 'underlined'
};
InputBase.propTypes = {
  /** This prop helps users to fill forms faster, especially on mobile devices. The name can be confusing, as it's more like an autofill. You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill). */
  autoComplete: PropTypes.string,
  /** If `true`, the input element is focused during the first mount. */
  autoFocus: PropTypes.bool,
  /** If `true`, the width of the component will automatically be set according to the contents inside the input, don't run with multiline = true */
  autoWidth: PropTypes.bool,
  /** Class for component. */
  className: PropTypes.string,
  /** The default value. Use when the component is not controlled. */
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** The number of milliseconds to wait before call onChange. */
  delayOnChange: PropTypes.number,
  /** If `true`, the component is disabled.*/
  disabled: PropTypes.bool,
  /** [Icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic--basic) or element placed to the right of input. */
  endIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Props of end icon. */
  endIconProps: PropTypes.object,
  /** Style inline of icon. */
  iconStyle: PropTypes.object,
  /** [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attributes) applied to the input element. */
  inputProps: PropTypes.object,
  /** Pass a ref to the input element. */
  inputRef: ref,
  /** Style inline of input element. */
  inputStyle: PropTypes.object,
  /** Maximum number of rows to display when multiline option is set to true. */
  maxRows: PropTypes.number,
  /** If `true`, a textarea element is rendered. */
  multiline: PropTypes.bool,
  /** Callback fired when the input is blurred. */
  onBlur: PropTypes.func,
  /** Callback fired when the value is changed. */
  onChange: PropTypes.func,
  /** Callback fired when the input is focused. */
  onFocus: PropTypes.func,
  /** Callback fired when input. */
  onInput: PropTypes.func,
  /** Callback fired when pressing a key. */
  onKeyDown: PropTypes.func,
  /** Callback fired when releases a key. */
  onKeyUp: PropTypes.func,
  /** The short hint displayed in the input before the user enters a value. */
  placeholder: PropTypes.string,
  /** If `true`, the component is readonly. */
  readOnly: PropTypes.bool,
  /** If `true`, the input element is required. */
  required: PropTypes.bool,
  /** allow resize multiline input (resize css style) */
  resize: PropTypes.string,
  /** Number of rows to display when multiline option is set to true. */
  rows: PropTypes.number,
  /** [Icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic--basic) or element placed to the left of input. */
  startIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Props of start icon. */
  startIconProps: PropTypes.object,
  /** Status type to display color for input. */
  status: PropTypes.oneOf(['default', 'warning', 'success', 'danger', 'info']),
  /** The value of the input element, required for a controlled component. */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** The variant to use. */
  viewType: PropTypes.oneOf(['underlined', 'outlined', 'none'])
};
export default InputBase;